import type { ServiceForSelectedServiceType } from '@common/interfaces/processStartWizard';
import { ServiceCode } from '@common/enums';
import { never } from '@common/utils/never';
import type { IServiceDataItem } from './getServiceData';

type ggzServiceCodeType =
    | ServiceCode.GGZOembl
    | ServiceCode.GGZOint
    | ServiceCode.GGZBther
    | ServiceCode.GGZCg
    | ServiceCode.GGZCt
    | ServiceCode.GGZCf2f
    | ServiceCode.GGZCmdo
    | ServiceCode.GGZCtriage
    | ServiceCode.GGZv;

export function getServiceDataForEpisodeMentalHealth(
    service: ServiceForSelectedServiceType,
    serviceCode: ggzServiceCodeType
): Partial<IServiceDataItem> {
    switch (serviceCode) {
        case ServiceCode.GGZOembl:
        case ServiceCode.GGZOint:
            return {
                costs: 'geen',
                steps: [
                    'Verstuur een digitale vragenlijst naar de patiënt',
                    'De vragenlijst geeft inzicht in de (ernst van de) klachten',
                    'Bekijk de score (en het advies) van de vragenlijst en sluit het proces af',
                    'Je ontvangt de uitslag via Edifact (in je HIS)',
                ],
                subTitle: (
                    <>
                        Voor het <strong>in beeld brengen van klachten</strong>
                    </>
                ),
                title: service.code === ServiceCode.GGZOembl ? 'GGZ Embloom' : 'GGZ Interapy',
                waitingTime: '1 dag',
            };

        case ServiceCode.GGZBther:
            return {
                costs: 'geen',
                steps: [
                    "Verstuur één of meerdere e-Health programma's",
                    'De patiënt maakt een account en start direct',
                    'De voortgang kan worden gemonitord. De patiënt kan beveiligd berichten sturen.',
                ],
                subTitle:
                    'Voor het begeleiden van de patiënt met psychische klachten met behulp van e-Health programma’s',
                title: 'Therapieland',
                waitingTime: 'verschilt per e-Health programma, patiënt en soort klacht',
            };

        case ServiceCode.GGZCg:
            return {
                costs: 'geen',
                isOrderedSteps: false,
                order: 1,
                steps: [
                    'Patiënt heeft veel last van bijwerkingen van citalopram. Wat adviseren jullie?',
                    'Patiënt heeft positieve ervaringen met EMDR. Zou dit zinvol zijn in deze nieuwe situatie?',
                    'Wat is het advies voor het afbouwen van quetiapine?',
                ],
                subTitle: 'Stel een gerichte, korte vraag aan een specialist die weinig context behoeft. Bijvoorbeeld:',
                title: 'Geschreven consult',
                visibleStepsCount: 2,
                waitingTime: ' 7 dagen',
            };

        case ServiceCode.GGZCt:
            return {
                costs: 'geen',
                isOrderedSteps: false,
                order: 2,
                steps: [
                    'De vooruitgang van de patiënt stokt. Wat zou ik verder kunnen doen binnen de huisartsenprakijk?',
                    'Graag wil ik telefonisch overleggen over het al dan niet verwijzen naar de GGZ',
                    'Graag wil ik telefonisch overleggen over de behandeling die ik alvast in kan zetten tijdens de overbrugging naar de GGZ',
                ],
                subTitle:
                    'Overleg telefonisch met een specialist, om meer context te geven bij een casus. Bijvoorbeeld:',
                title: 'Telefonisch consult',
                visibleStepsCount: 2,
                waitingTime: ' 7 dagen',
            };

        case ServiceCode.GGZCf2f:
            return {
                costs: 'geen',
                isOrderedSteps: false,
                order: 3,
                steps: [
                    'Er is sprake van diverse terugkerende klachten. Het verzoek om mee te kijken of een behandeling binnen de GGZ gewenst is.',
                    'Deel je mijn vermoeden van ASS? Denk je dat het zin heeft om verdere diagnostiek in gang te zetten?',
                    'Patiënt met schizofrenie slikt al jaren antipsychotica en is stabiel. Graag beoordelen of de medicatie afgebouwd kan worden.',
                ],
                subTitle:
                    'De patiënt ziet de specialist eenmalig fysiek voor een zo volledig mogelijk antwoord op je uitgebreide of complexe vragen. Bijvoorbeeld:',
                title: 'Face to face consult',
                visibleStepsCount: 1,
                waitingTime: '3 weken',
            };

        case ServiceCode.GGZCmdo:
            return {
                costs: 'geen',
                isOrderedSteps: false,
                order: 4,
                steps: [
                    'De vooruitgang stokt. Wat zou ik verder kunnen doen binnen de huisartsenpraktijk?',
                    'Graag wil ik overleggen over het al dan niet verwijzen naar de GGZ',
                    'Graag wil ik overleggen over de opties voor een patiënt die tussen wal en schip valt',
                ],
                subTitle:
                    'Bespreek met een specialist in een uur een aantal patiënten. Dien hier per patiënt een aanvraag in voor een MDO. Bijvoorbeeld:',
                title: 'Multidisciplinair overleg',
                visibleStepsCount: 1,
                waitingTime: 'zelf in te plannen met de specialist. Dit gebeurt niet binnen het Ksyos platform. ',
            };

        case ServiceCode.GGZCtriage:
            return {
                costs: 'geen',
                isOrderedSteps: false,
                order: 5,
                steps: [
                    'Er is sprake van angst en somberheidsklachten. Graag jullie triagering voor het echelon-advies en behandelrichting.',
                    'Er speelt veel bij deze patiënt. Welke problematiek is voorliggend?',
                    'Is er sprake van onderliggende ontwikkelings- of persoonlijkheidspathologie? En welke invloed heeft dit op het echelon-advies?',
                ],
                subTitle:
                    'De patiënt wordt gescreend door Embloom middels een TeleScreen vragenlijst en een telefonische afspraak met een triagepsycholoog. Bijvoorbeeld:',
                title: 'Triageconsult',
                visibleStepsCount: 1,
                waitingTime: '3 weken',
            };

        case ServiceCode.GGZv:
            return {
                costs: 'geen',
                steps: [
                    'Verstuur een digitale verwijzing naar een instelling aangesloten bij Ksyos',
                    'De instelling verwerkt de verwijzing',
                ],
                subTitle: 'Verwijs jouw patiënt naar de basis GGZ of specialistische GGZ',
                title: 'Verwijzing',
                waitingTime: 'n.v.t.',
            };

        default:
            never(serviceCode);
    }
}
